import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, IconButton, Tooltip, Modal } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

export default function ImageGallery({ images = [], productName = "" }) {
  const normalizeImages = (arr) =>
    Array.from(
      new Set(
        (arr || [])
          .flatMap((item) => {
            if (!item) return [];
            if (typeof item === "string") return [item];
            if (typeof item === "object") {
              if (typeof item.url === "string") return [item.url];
              if (typeof item.image === "string") return [item.image];
              if (item.image && typeof item.image.url === "string") return [item.image.url];
            }
            return [];
          })
          .filter(Boolean)
      )
    );

  const imgs = useMemo(() => normalizeImages(images), [images]);

  const [selectedImage, setSelectedImage] = useState(imgs[0] || null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [openModal, setOpenModal] = useState(false);
  const [modalScale, setModalScale] = useState(1);

  const imgContainerRef = useRef(null);
  const thumbContainerRef = useRef(null);

  useEffect(() => {
    if (!imgs.length) {
      setSelectedImage(null);
      return;
    }
    if (!selectedImage || !imgs.includes(selectedImage)) {
      setSelectedImage(imgs[0]);
      setIsZoomed(false);
    }
  }, [imgs]);

  const handleMouseEnter = () => imgs.length && setIsZoomed(true);
  const handleMouseLeave = () => setIsZoomed(false);
  const handleMouseMove = (e) => {
    if (!isZoomed || !imgContainerRef.current) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const scrollThumbnails = (dir) => {
    const el = thumbContainerRef.current;
    if (!el) return;
    const step = el.clientWidth;
    let next = dir === "left" ? el.scrollLeft + step : el.scrollLeft - step;
    if (dir === "left" && el.scrollLeft + step >= el.scrollWidth - el.clientWidth) next = 0;
    if (dir === "right" && el.scrollLeft === 0) next = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: next, behavior: "smooth" });
  };

  const goPrev = () => {
    if (!imgs.length) return;
    const idx = imgs.findIndex((i) => i === selectedImage);
    const prevIdx = (idx - 1 + imgs.length) % imgs.length;
    setSelectedImage(imgs[prevIdx]);
    setModalScale(1);
  };

  const goNext = () => {
    if (!imgs.length) return;
    const idx = imgs.findIndex((i) => i === selectedImage);
    const nextIdx = (idx + 1) % imgs.length;
    setSelectedImage(imgs[nextIdx]);
    setModalScale(1);
  };

  const navSize = 80;
  const navBoxSize = navSize / 2;
  const centerX = (zoomPosition.x / 100) * navSize;
  const centerY = (zoomPosition.y / 100) * navSize;

  const placeholder =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'>
        <rect width='100%' height='100%' fill='#f5f5f5'/>
        <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#9e9e9e' font-size='16'>Sin imagen</text>
      </svg>`
    );

  return (
    <>
      <Box
        ref={imgContainerRef}
        sx={{
          width: "100%",
          maxWidth: 400,
          height: 400,
          overflow: "hidden",
          border: "1px solid #ddd",
          borderRadius: 2,
          position: "relative",
          cursor: imgs.length ? "zoom-in" : "default",
          backgroundColor: "#fff",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <Box
          component="img"
          src={selectedImage || placeholder}
          alt={productName}
          onError={(e) => {
            e.currentTarget.src = placeholder;
          }}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transition: "transform 0.1s ease",
            transform: imgs.length && isZoomed
              ? `scale(2) translate(${50 - zoomPosition.x}%, ${50 - zoomPosition.y}%)`
              : "scale(1)",
            transformOrigin: "center center",
          }}
        />
        {imgs.length > 0 && (
          <Tooltip title="Ver imagen completa">
            <IconButton
              onClick={() => {
                setModalScale(1);
                setOpenModal(true);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.4)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        )}
        {imgs.length > 0 && isZoomed && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: 8,
              width: navSize,
              height: navSize,
              border: "1px solid #ccc",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "#fff",
              p: 0.5,
            }}
          >
            <Box component="img" src={selectedImage} alt="nav" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
            <Box
              sx={{
                position: "absolute",
                border: "1px solid #007B83",
                width: navBoxSize,
                height: navBoxSize,
                left: centerX - navBoxSize / 2,
                top: centerY - navBoxSize / 2,
                pointerEvents: "none",
              }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ position: "relative", mt: 2, display: "flex", alignItems: "center" }}>
        <IconButton size="small" onClick={() => scrollThumbnails("left")} sx={{ mr: 1 }} disabled={!imgs.length}>
          <ArrowBackIosNewIcon fontSize="inherit" />
        </IconButton>
        <Box
          ref={thumbContainerRef}
          sx={{
            overflowX: "auto",
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            flexGrow: 1,
          }}
        >
          {imgs.map((img, idx) => (
            <Box
              key={idx}
              component="img"
              src={img}
              alt={`${productName}-${idx}`}
              onClick={() => {
                setSelectedImage(img);
                setIsZoomed(false);
              }}
              onError={(e) => {
                e.currentTarget.src = placeholder;
              }}
              sx={{
                width: 60,
                height: 60,
                objectFit: "contain",
                borderRadius: 1,
                border: selectedImage === img ? "2px solid #3f3f3f" : "1px solid #ccc",
                mx: 0.5,
                cursor: "pointer",
                display: "inline-block",
                backgroundColor: "#fff",
              }}
            />
          ))}
        </Box>
        <IconButton size="small" onClick={() => scrollThumbnails("right")} sx={{ ml: 1 }} disabled={!imgs.length}>
          <ArrowForwardIosIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.85)",
            position: "relative",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.3)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
            onClick={() => setOpenModal(false)}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            onClick={goPrev}
            sx={{
              position: "absolute",
              left: 20,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.3)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
            disabled={!imgs.length}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <IconButton
            onClick={goNext}
            sx={{
              position: "absolute",
              right: 20,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.3)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
            disabled={!imgs.length}
          >
            <ArrowForwardIosIcon />
          </IconButton>
          <Box
            component="img"
            src={selectedImage || placeholder}
            alt="zoomed"
            onError={(e) => {
              e.currentTarget.src = placeholder;
            }}
            sx={{
              maxWidth: "80%",
              maxHeight: "80%",
              objectFit: "contain",
              borderRadius: 2,
              transform: `scale(${modalScale})`,
              transition: "transform 0.2s ease",
              backgroundColor: "#fff",
            }}
          />
          <Box sx={{ position: "absolute", bottom: 40, display: "flex", gap: 2 }}>
            <IconButton
              onClick={() => setModalScale((prev) => Math.max(1, prev - 0.5))}
              sx={{
                bgcolor: "rgba(0,0,0,0.3)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
              }}
            >
              <RemoveIcon />
            </IconButton>
            <IconButton
              onClick={() => setModalScale((prev) => Math.min(4, prev + 0.5))}
              sx={{
                bgcolor: "rgba(0,0,0,0.3)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
